import type { ErrorInfo, ReactNode } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { logger } from "@common/utils/diagnostics";
import ErrorBoundary from "./ErrorBoundary";
import TopBar from "./TopBar";

/** A crash goes into the logger, so the bug report carries it. */
export const logCrash = (error: Error, info: ErrorInfo) => logger.error(`[Crash] ${error.message}`, info.componentStack ?? "");

/**
 * The crash screen, keyed on the route's section (the first path segment):
 * moving to another page clears it, and a change deeper in the path doesn't
 * remount the page. Report This opens the bug report with the crash in it;
 * the note rides in the navigation state, and the move is in-app so the
 * logs from before the crash go with it.
 */
export function PageBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (
    <ErrorBoundary
      key={pathname.split("/")[1]}
      onError={logCrash}
      onReport={(crashNote) => navigate("/bug-report", { state: { crashNote } })}
      buttonClassName="btn"
      primaryButtonClassName="btn primary"
    >
      {children}
    </ErrorBoundary>
  );
}

/** Every page but the player: the top bar, and the page below it, where a crash stays. */
export function SiteLayout() {
  const { pathname } = useLocation();
  return (
    <div className={`app ${pathname === "/" ? "home" : "about"}`}>
      <TopBar />
      <main>
        <PageBoundary>
          <Outlet />
        </PageBoundary>
      </main>
    </div>
  );
}
